import { useContext, useEffect, useState } from 'react';
import * as z from 'zod';
import { getRouteApi } from '@tanstack/react-router';
import { DateTime } from 'luxon';

import config from '@/config';

import { ACCESS_TOKEN_KEY, USER_ROLE, APP_USER_ID } from '@/constants';
import {
	AUTH_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	parseJwt,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
} from '@views/home/auth/Authentication.hooks';
import { AppContext } from '@/App.context';
import { useQueryClient } from '@tanstack/react-query';

const routeApi = getRouteApi('/callback');

export const callbackSearchSchema = z.object({
	code: z.string(),
	state: z.string(),
});

type CallbackSearch = z.infer<typeof callbackSearchSchema>;

export function useCallback(): CallbackResponse {
	const queryClient = useQueryClient();

	const routeSearch: CallbackSearch = routeApi.useSearch();
	const { code, state } = routeSearch;
	const [url, setUrl] = useState('');
	const { kv, queryRequest, clearCache } = useContext(AppContext);
	const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | UserNotFoundError | null>(null);

	const clearData = () => {
		queryClient.removeQueries();
		if (clearCache) {
			clearCache
				.then(() => {
					console.log('Cache cleared');
				})
				.catch(() => {
					console.error('Error clearing cache');
				});
		}
	};

	useEffect(() => {
		if (!code) {
			setError(new Error('Missing code'));
			setIsLoading(false);
			return;
		}

		const redirecUrl = [
			location.protocol,
			'//',
			location.host,
			location.pathname,
		].join('');

		const params = new URLSearchParams({
			code: `${code}`,
			redirect_uri: redirecUrl,
		});

		const url = [
			config.build.host,
			'/auth/authorize',
			'?',
			params.toString(),
		].join('');
		fetch(url, {
			method: 'GET',
			headers: {
				'app-key': config.build.appKey,
				'Content-Type': 'application/json',
			},
		})
			.then((r) => r.json())
			.then((r) => {
				let forwardURL = '/';
				try {
					forwardURL = state ? atob(state) : '/';
				} catch (e) {
					forwardURL = '/'
				}
				clearData();
				kv.clear();
				if (r.error) {
					const { code } = r;
					if (code === 'USER_NOT_FOUND') {
						setError(new UserNotFoundError('User not found'));
						setIsLoading(false);
						return;
					}

					setError(new Error(r.error));
					setIsLoading(false);
					return;
				}

				saveTokens(r);

				if (localStorage.getItem(IS_SUBSCRIBED_KEY) === 'true') {
					setIsSubscribed(true);
				}

				queryRequest.rebuild();

				setUrl(forwardURL);
				setIsLoading(false);
			})
			.catch((e) => {
				clearData();
				setError(e);
				setIsLoading(false);
			});
	}, [code]);

	return {
		isLoading,
		error,
		isSubscribed,
		url,
	};
}

export class UserNotFoundError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'UserNotFound';
	}
}

export function saveTokens(content: any) {
	const { access_token, refresh_token, expires } = content;

	// JWT Parse Data
	const accessTokenData = parseJwt(access_token);
	const refreshTokenData = parseJwt(refresh_token);

	// Calculate expiration dates
	const accessExpirationDate = DateTime.now()
		.plus({ seconds: expires })
		.toISO();
	const refreshExpirationDate = DateTime.fromSeconds(
		refreshTokenData.exp,
	).toISO();

	clearTokenData();

	// Store in localStorage
	localStorage.setItem(AUTH_KEY, 'true');
	localStorage.setItem(USER_ROLE, 'user');
	if (accessTokenData.sub) {
		localStorage.setItem(APP_USER_ID, accessTokenData.sub);
	} else {
		if (localStorage.getItem(APP_USER_ID)) {
			localStorage.removeItem(APP_USER_ID);
		}
	}

	localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
	localStorage.setItem(ACCESS_TOKEN_EXPIRATION_KEY, accessExpirationDate);

	localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
	if (refreshExpirationDate) {
		localStorage.setItem(
			REFRESH_TOKEN_EXPIRATION_KEY,
			refreshExpirationDate,
		);
	}

	if (accessTokenData.entitlements) {
		if (accessTokenData.entitlements?.subscription) {
			localStorage.setItem(IS_SUBSCRIBED_KEY, 'true');
		}
		if (accessTokenData.entitlements.features) {
			localStorage.setItem(
				FEATURES_KEY,
				JSON.stringify(accessTokenData.entitlements.features),
			);
		}
	}
}

function clearTokenData() {
	const keys = [
		AUTH_KEY,
		USER_ROLE,
		ACCESS_TOKEN_KEY,
		ACCESS_TOKEN_EXPIRATION_KEY,
		REFRESH_TOKEN_KEY,
		REFRESH_TOKEN_EXPIRATION_KEY,
		IS_SUBSCRIBED_KEY,
		FEATURES_KEY,
	];

	for (const key of keys) {
		if (localStorage.getItem(key)) {
			localStorage.removeItem(key);
		}
	}
}

export interface CallbackResponse {
	isLoading: boolean;
	error: Error | null;
	isSubscribed?: boolean;
	url: string;
}
